import { clamp } from 'lodash';

// Utils
import { getDomElementId, getEditorDomElementId } from '../../../element/utils/elementUtil';
import { translateDOMRectIntoScrollableParentCoordinates } from '../../../utils/dom/scrollableParentUtils';
import { getHighlightedFromTableDOM } from '../../../element/table/utils/tableDOMUtils';

// Constants
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import { TABLE_CELL_EDITOR_KEY, TABLE_GRID_EDITOR_KEY } from '../../../../common/table/tableConstants';

const SCROLL_PADDING_X = 50;
const SCROLL_PADDING_Y = 50;

enum ScrollToElementMode {
    CENTER = 'center',
    EDGE = 'left',
}

export type ScrollToElementConfig = {
    scrollableParent: HTMLElement;
    scrollTargetRect?: Rect | undefined;
    editorFocusRect?: Rect | undefined;

    // The height of the obstruction at the bottom of the scrollable parent (e.g. sheet, keyboard)
    bottomObstructionHeight: number;
};

export const getContentDomElement = (elementId: string, editorKey?: string) => {
    // If there is a separate editor component open for this element, use that
    const editorDomId = getEditorDomElementId(elementId);
    const editorElement = document.getElementById(editorDomId);
    if (editorElement) return editorElement;

    const elementDomId = getDomElementId(elementId);
    const elementDom = document.getElementById(elementDomId);

    // For table cells, we want to return the table cell DOM instead of the whole table element
    if (elementDom && (editorKey === TABLE_CELL_EDITOR_KEY || editorKey === TABLE_GRID_EDITOR_KEY)) {
        return getHighlightedFromTableDOM(elementDom);
    }

    // Otherwise, use the element itself
    return elementDom;
};

/**
 * Get the scrollable parent rect without the obstruction at the bottom of the screen (e.g. sheet/virtual keyboard)
 */
const getScrollableParentRectWithoutObstruction = (
    scrollableParentRect: DOMRect,
    bottomObstructionHeight: number,
    windowInnerHeight: number = window.innerHeight,
): DOMRect => {
    const newHeight = windowInnerHeight - scrollableParentRect.top - bottomObstructionHeight;
    if (newHeight < 0) return scrollableParentRect;

    // Since scrollableParent doesn't cover the primary toolbar and bottom safe area, whereas bottomObstructionHeight does,
    // we want to get the height of scrollableParent that spans to the bottom of the page,
    // in order to get an accurate pageViewHeight
    return new DOMRect(scrollableParentRect.x, scrollableParentRect.y, scrollableParentRect.width, newHeight);
};

/**
 * Get target scrollLeft that horizontally centers the element that's being edited.
 */
const getTargetScrollLeftToAlignElementToCenter = (
    scrollableParent: HTMLElement,
    scrollableParentRect: Rect,
    scrollTargetRect: Rect,
    editorFocusRect: Rect | undefined,
) => {
    const relativeScrollTargetRect = translateDOMRectIntoScrollableParentCoordinates(
        scrollableParent,
        scrollTargetRect,
    );

    // 1. If the element is smaller than the scrollableParent view, we want to make sure that the element is
    //    contained within the scrollableParent view
    const isElementSmallerThanPageView = relativeScrollTargetRect.width < scrollableParentRect.width;
    if (isElementSmallerThanPageView) {
        const centerOffsetX = (scrollableParentRect.width - relativeScrollTargetRect.width) / 2;
        return relativeScrollTargetRect.left - centerOffsetX;
    }

    // 2. If the element is larger than the scrollableParent view, when editorFocusRect is not provided,
    //    scroll to the top of the element (with padding)
    if (!editorFocusRect) return relativeScrollTargetRect.left - SCROLL_PADDING_X;

    // 3. Otherwise, scroll to the editorFocusRect point
    return clamp(
        scrollableParent.scrollLeft + editorFocusRect.x - scrollableParentRect.left - SCROLL_PADDING_X,
        relativeScrollTargetRect.right - scrollableParentRect.width + SCROLL_PADDING_X,
    );
};

/**
 * Get target scrollLeft that place the edited element near the closest edge of the parent rect,
 * so that the whole width of the element is visible on screen.
 */
const getTargetScrollLeftToAlignElementToEdge = (
    scrollableParent: HTMLElement,
    scrollableParentRect: Rect,
    scrollTargetRect: Rect,
    editorFocusRect: Rect | undefined,
) => {
    const targetRect = editorFocusRect || scrollTargetRect;

    // If element is placed past the left edge of the scrollable parent
    if (targetRect.left - SCROLL_PADDING_X < scrollableParentRect.left) {
        return scrollableParent.scrollLeft + (targetRect.left - scrollableParentRect.left) - SCROLL_PADDING_X;
    }

    // If element is placed past the right edge of the scrollable parent
    if (targetRect.right + SCROLL_PADDING_X > scrollableParentRect.right) {
        return (
            scrollableParent.scrollLeft +
            (targetRect.right - scrollableParentRect.left - scrollableParentRect.width) +
            SCROLL_PADDING_X
        );
    }

    return scrollableParent.scrollLeft;
};

/**
 * Get target scrollTop that vertically centers the element that's being edited.
 */
const getTargetScrollTopToAlignElementToCenter = (
    scrollableParent: HTMLElement,
    scrollableParentRect: Rect,
    scrollTargetRect: Rect,
    editorFocusRect: Rect | undefined,
) => {
    const relativeScrollTargetRect = translateDOMRectIntoScrollableParentCoordinates(
        scrollableParent,
        scrollTargetRect,
    );

    // 1. If the element is smaller than the scrollableParent view, we want to make sure that the element is
    //    contained within the scrollableParent view
    const isElementSmallerThanPageView = relativeScrollTargetRect.height < scrollableParentRect.height;
    if (isElementSmallerThanPageView) {
        const centerOffsetY = (scrollableParentRect.height - relativeScrollTargetRect.height) / 2;
        return relativeScrollTargetRect.top - centerOffsetY;
    }

    // 2. If the element is larger than the scrollableParent view, when editorFocusRect is not provided,
    //    scroll to the top of the element (with padding)
    if (!editorFocusRect) return relativeScrollTargetRect.top - SCROLL_PADDING_Y;

    // 3. Otherwise, scroll to the editorFocusRect point
    return scrollableParent.scrollTop + editorFocusRect.y - scrollableParentRect.top - scrollableParentRect.height / 2;
};

/**
 * Get target scrollTop that place the edited element near the closest edge of the parent rect,
 * so that the whole height of the element is visible on screen.
 */
const getTargetScrollTopToAlignElementToEdge = (
    scrollableParent: HTMLElement,
    scrollableParentRect: Rect,
    scrollTargetRect: Rect,
    editorFocusRect: Rect | undefined,
) => {
    const targetRect = editorFocusRect || scrollTargetRect;

    // If element is placed past the top edge of the scrollable parent
    if (targetRect.top - SCROLL_PADDING_Y < scrollableParentRect.top) {
        return scrollableParent.scrollTop + (targetRect.top - scrollableParentRect.top) - SCROLL_PADDING_Y;
    }

    // If element is placed past the bottom edge of the scrollable parent
    if (targetRect.bottom + SCROLL_PADDING_Y > scrollableParentRect.bottom) {
        return (
            scrollableParent.scrollTop +
            (targetRect.bottom - scrollableParentRect.top - scrollableParentRect.height) +
            SCROLL_PADDING_Y
        );
    }

    return scrollableParent.scrollTop;
};

const getTargetScrollLeftFn = (mode: ScrollToElementMode) =>
    mode === ScrollToElementMode.CENTER
        ? getTargetScrollLeftToAlignElementToCenter
        : getTargetScrollLeftToAlignElementToEdge;

const getTargetScrollTopFn = (mode: ScrollToElementMode) =>
    mode === ScrollToElementMode.CENTER
        ? getTargetScrollTopToAlignElementToCenter
        : getTargetScrollTopToAlignElementToEdge;
/**
 * Determines the target scroll position for the scrollable element.
 */
export const getTargetScrollToElementFn =
    (mode: ScrollToElementMode) =>
    ({
        scrollableParent,
        scrollTargetRect,
        editorFocusRect,
        bottomObstructionHeight,
    }: ScrollToElementConfig & { scrollTargetRect: Rect }): { scrollLeft: number; scrollTop: number } => {
        const scrollableParentRect = scrollableParent.getBoundingClientRect();
        const scrollableParentRectWithoutObstruction = getScrollableParentRectWithoutObstruction(
            scrollableParentRect,
            bottomObstructionHeight,
        );

        const getTargetScrollLeft = getTargetScrollLeftFn(mode);
        const targetScrollLeft = getTargetScrollLeft(
            scrollableParent,
            scrollableParentRectWithoutObstruction,
            scrollTargetRect,
            editorFocusRect,
        );

        const getTargetScrollTop = getTargetScrollTopFn(mode);
        const targetScrollTop = getTargetScrollTop(
            scrollableParent,
            scrollableParentRectWithoutObstruction,
            scrollTargetRect,
            editorFocusRect,
        );

        const maxScrollLeft = scrollableParent.scrollWidth - scrollableParentRect.width;
        const maxScrollTop = scrollableParent.scrollHeight - scrollableParentRect.height;

        // Clamp the target scroll values to the scrollable element's bounds
        return {
            scrollLeft: clamp(targetScrollLeft, 0, maxScrollLeft),
            scrollTop: clamp(targetScrollTop, 0, maxScrollTop),
        };
    };

export const getTargetScrollToAlignElementToCenter = getTargetScrollToElementFn(ScrollToElementMode.CENTER);
export const getTargetScrollToAlignElementToEdge = getTargetScrollToElementFn(ScrollToElementMode.EDGE);
